// src/pages/auth/Login.tsx
// Login page — email/password form wired to the session-based auth backend.
import { useState, useEffect } from 'react'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { Navigate, useNavigate, Link } from 'react-router-dom'
import { Mail, Lock, Eye, EyeOff, Loader2 } from 'lucide-react'
import toast from '@/utils/toast'
import { useLoginMutation } from '@/features/auth/auth.api'
import { loginSchema, type LoginFormValues } from '@/features/auth/login.schema'
import { useAuth } from '@/app/hooks'
import Logo from '../../assets/logo2.png'
import loginImage from '../../assets/login_img1.png'

const sanitizeEmail = (value: string) => {
  // Allow only ASCII characters except spaces (ASCII 33-126)
  return value.replace(/[^\x21-\x7E]/g, '')
}

const sanitizePassword = (value: string) => {
  // Allow standard ASCII characters (printable ASCII 32-126) including spaces
  return value.replace(/[^\x20-\x7E]/g, '')
}

export default function Login() {
  const navigate = useNavigate()
  const { isAuthenticated, user } = useAuth()
  const [login, { isLoading }] = useLoginMutation()
  const [showPassword, setShowPassword] = useState(false)

  const {
    register,
    handleSubmit,
    setValue,
    formState: { errors },
  } = useForm<LoginFormValues>({
    resolver: zodResolver(loginSchema),
    mode: 'onTouched',
    shouldFocusError: false,
  })

  const syncAutofillValues = (forceValidate = false) => {
    const emailInput = document.getElementById('email') as HTMLInputElement | null
    const passwordInput = document.getElementById('password') as HTMLInputElement | null

    if (emailInput && (emailInput.value || forceValidate)) {
      setValue('email', sanitizeEmail(emailInput.value), { shouldValidate: forceValidate || !!emailInput.value })
    }
    if (passwordInput && (passwordInput.value || forceValidate)) {
      setValue('password', sanitizePassword(passwordInput.value), { shouldValidate: forceValidate || !!passwordInput.value })
    }
  }

  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        syncAutofillValues(false)
      }
    }

    const handleWindowFocus = () => syncAutofillValues(false)

    document.addEventListener('visibilitychange', handleVisibilityChange)
    window.addEventListener('focus', handleWindowFocus)

    // Sync on mount and periodically after mount to catch standard browser autofill
    syncAutofillValues(false)
    const timers = [
      setTimeout(() => syncAutofillValues(false), 100),
      setTimeout(() => syncAutofillValues(false), 300),
      setTimeout(() => syncAutofillValues(false), 500),
      setTimeout(() => syncAutofillValues(false), 1000),
    ]

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange)
      window.removeEventListener('focus', handleWindowFocus)
      timers.forEach(clearTimeout)
    }
  }, [])

  // Already authenticated — redirect role-appropriately
  const isStudent =
    user?.role?.includes('STUDENT') ||
    user?.primaryRole?.includes('STUDENT') ||
    user?.roles?.some((r) => r.includes('STUDENT')) ||
    user?.userType === 'STUDENT'

  if (isAuthenticated) {
    return <Navigate to={isStudent ? '/student/courses' : '/dashboard'} replace />
  }

  const onSubmit = async (data: LoginFormValues) => {
    try {
      toast.dismiss('login-error')
      const res = await login(data).unwrap()
      const loggedUser = res?.data
      const isStudentUser =
        loggedUser?.role?.includes('STUDENT') ||
        loggedUser?.primaryRole?.includes('STUDENT') ||
        loggedUser?.roles?.some((r: any) => String(r).includes('STUDENT')) ||
        loggedUser?.userType === 'STUDENT'

      if (isStudentUser) {
        navigate('/student/courses')
      } else {
        navigate('/dashboard')
      }
    } catch (err: unknown) {
      const error = err as {
        data?: {
          message?: string
          error?: { code?: string; message?: string }
          code?: string
        }
      }
      const code = error?.data?.error?.code || error?.data?.code
      if (code === 'SEC_0005') {
        toast.error('Invalid email or password.', { id: 'login-error' })
      } else {
        toast.error(error?.data?.message || error?.data?.error?.message || 'Login failed. Please try again.', {
          id: 'login-error',
        })
      }
    }
  }

  return (
    <div className="h-screen w-full bg-white flex flex-col lg:flex-row font-sans overflow-hidden select-none">

      {/* LEFT SIDE */}
      <div className="w-full lg:w-5/12 flex flex-col justify-between p-6 sm:p-10 lg:p-12 xl:p-20 h-full bg-white z-20 overflow-hidden">

        <div className="max-w-md w-full mx-auto my-auto py-3 sm:py-6 flex-1 flex flex-col justify-center overflow-hidden max-h-[85vh] lg:max-h-none">

          {/* Header: Logo + Title */}
          <div className="flex flex-col items-start justify-start text-left mb-6 w-full">

            <div className="sm:-ml-[22px] -ml-[12px] -mb-[12px] flex justify-start items-center p-0 select-none w-full">
              <img
                className="w-full max-w-[120px] sm:max-w-[140px] h-auto object-contain object-left block m-0 p-0"
                src={Logo}
                alt="Dualon Logo"
              />
            </div>

            <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-1.5 tracking-tight">
              Log in to your Account
            </h1>
            <p className="text-gray-400 text-xs sm:text-sm font-normal">
              Welcome back! Enter your credentials to continue.
            </p>
          </div>

          {/* Login Form */}
          <form className="space-y-4 sm:space-y-5" onSubmit={handleSubmit(onSubmit)} noValidate>

            {/* Email Field */}
            <div className="space-y-1.5 text-left">
              <label htmlFor="email" className="text-xs sm:text-sm font-semibold text-gray-900 block">
                Email <span className="text-red-500">*</span>
              </label>
              <div className="relative flex items-center">
                <Mail className="w-4 h-4 sm:w-5 sm:h-5 text-gray-400 absolute left-3 pointer-events-none" />
                <input
                  id="email"
                  type="email"
                  autoComplete="email"
                  placeholder="Enter your email"
                  {...register('email', {
                    onChange: (e) => {
                      e.target.value = sanitizeEmail(e.target.value)
                    }
                  })}
                  disabled={isLoading}
                  aria-invalid={errors.email ? 'true' : 'false'}
                  aria-describedby={errors.email ? 'email-error' : undefined}
                  className={`w-full pl-10 pr-4 py-2.5 sm:py-3 rounded-xl border transition-colors text-xs text-gray-900 sm:text-sm placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-brand-500/20 disabled:bg-gray-50 disabled:text-gray-500 ${errors.email
                      ? 'border-red-500 focus:border-red-500 focus:ring-red-500/20'
                      : 'border-gray-200 focus:border-brand-500'
                    }`}
                />
              </div>
              {errors.email && (
                <p id="email-error" className="text-red-500 text-[11px] sm:text-xs mt-1 font-medium" role="alert">
                  {errors.email.message}
                </p>
              )}
            </div>

            {/* Password Field */}
            <div className="space-y-1.5 text-left">
              <div className="flex items-center justify-between">
                <label htmlFor="password" className="text-xs sm:text-sm font-semibold text-gray-900 block">
                  Password <span className="text-red-500">*</span>
                </label>
                <Link
                  to="/forgot-password"
                  className="text-xs font-semibold text-brand-500 hover:text-brand-600 transition-colors"
                >
                  Forgot password?
                </Link>
              </div>
              <div className="relative flex items-center">
                <Lock className="w-4 h-4 sm:w-5 sm:h-5 text-gray-400 absolute left-3 pointer-events-none" />
                <input
                  id="password"
                  type={showPassword ? 'text' : 'password'}
                  autoComplete="current-password"
                  placeholder="Enter your password"
                  {...register('password', {
                    onChange: (e) => {
                      e.target.value = sanitizePassword(e.target.value)
                    }
                  })}
                  disabled={isLoading}
                  aria-invalid={errors.password ? 'true' : 'false'}
                  aria-describedby={errors.password ? 'password-error' : undefined}
                  className={`w-full pl-10 pr-12 py-2.5 sm:py-3 rounded-xl border transition-colors text-xs text-gray-900 sm:text-sm placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-brand-500/20 disabled:bg-gray-50 disabled:text-gray-500 ${errors.password
                      ? 'border-red-500 focus:border-red-500 focus:ring-red-500/20'
                      : 'border-gray-200 focus:border-brand-500'
                    }`}
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  disabled={isLoading}
                  className="absolute right-3 p-1 text-gray-400 hover:text-gray-600 transition-colors focus:outline-none disabled:pointer-events-none"
                  aria-label={showPassword ? 'Hide password' : 'Show password'}
                >
                  {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
              {errors.password && (
                <p id="password-error" className="text-red-500 text-[11px] sm:text-xs mt-1 font-medium" role="alert">
                  {errors.password.message}
                </p>
              )}
            </div>

            {/* Submit Button */}
            <button
              id="login-submit"
              type="submit"
              disabled={isLoading}
              onClick={() => {
                syncAutofillValues(true)
                window.getSelection()?.removeAllRanges()
              }}
              className="w-full bg-brand-500 hover:bg-brand-600 text-white font-semibold py-2.5 sm:py-3 rounded-xl transition-all duration-200 mt-6 shadow-md hover:shadow-lg disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2 focus:outline-none focus:ring-2 focus:ring-brand-500/20"
            >
              {isLoading ? (
                <>
                  <Loader2 className="animate-spin h-4 w-4 text-white" />
                  <span className="text-xs sm:text-sm">Signing in...</span>
                </>
              ) : (
                <span className="text-xs sm:text-sm">Sign In</span>
              )}
            </button>

            {/* New Student Register Link */}
            <div className="mt-5 text-center">
              <span className="text-xs text-neutral-500">New student? </span>
              <Link to="/register" className="text-xs font-semibold text-emerald-600 hover:text-emerald-700 hover:underline">
                Register here
              </Link>
            </div>
          </form>
        </div>
      </div>

      {/* RIGHT SIDE */}
      <div className="hidden lg:flex lg:w-7/12 relative bg-[#010519] flex-col justify-end overflow-hidden">
        <img
          src={loginImage}
          alt="Login Visual"
          loading="eager"
          decoding="async"
          className="absolute inset-0 w-full h-full object-contain object-right-top pl-8 z-0 transition-transform duration-700 hover:scale-102"
        />
        <div className="relative z-10 p-16 max-w-2xl text-left bg-gradient-to-t from-[#010519] via-[#010519]/75 to-transparent">
          <h2 className="text-4xl font-semibold text-white tracking-tight leading-tight">
            Advanced Learning, <br /> Built for You.
          </h2>
          <p className="mt-4 text-base text-gray-400 max-w-xl leading-relaxed">
            Dualon is your unified hub for digital education. Access institution-curated syllabi, comprehensive study resources, and real-time exam schedules with a platform designed to empower your learning journey.
          </p>
        </div>
      </div>

    </div>
  )
}